"use client";

import type React from "react";
import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Chip,
  Divider,
  Stack,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Person, Phone, CalendarToday, Badge } from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";

interface CallDetailDialogProps {
  open: boolean;
  onClose: () => void;
  data: any;
}

const CallDetailDialog: React.FC<CallDetailDialogProps> = ({
  open,
  onClose,
  data,
}) => {
  const navigate = useNavigate();
  const [replyText, setReplyText] = useState("");
  const [isReplyMode, setIsReplyMode] = useState(false);

  const calculateAge = (dob: string): number => {
    const birthDate = new Date(dob);
    const ageDiff = Date.now() - birthDate.getTime();
    const ageDate = new Date(ageDiff);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  };
  console.log("Call Detail Data in CallDetailDialog:", data);

  const handleReplyFromDashboard = () => {
    navigate("/ipd-details", {
      state: { otherTabsData: data?.otherTabsData },
    });
    onClose();
  };

  const handleReplyHere = () => {
    setIsReplyMode(true);
  };

  const handleSubmitReply = async () => {
    try {
      // Mock API call - replace with your actual API endpoint
      const response = await axios.patch(
        `${process.env.REACT_APP_IPD_API_URL}/ipd-form-data/updateCallToDrResponse/${data?.formId}`,
        {
          response: replyText,
          index: data?.replyData?.index,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data?.isSuccess === true) {
        toast.success(
          response.data?.message || "Reply submitted successfully!"
        );
        setReplyText("");
        setIsReplyMode(false);
        onClose();
      } else {
        toast.error(response.data?.message || "Failed to submit reply.");
        console.error("Error submitting reply:", response.data?.message);
      }
    } catch (error) {
      console.error("Error submitting reply:", error);
    }
  };
  const InfoItem = ({
    icon,
    label,
    value,
  }: {
    icon: React.ReactNode;
    label: string;
    value: string;
  }) => (
    <Box display="flex" alignItems="center" gap={1}>
      {icon}
      <Typography variant="body2" color="text.secondary">
        {label}:
      </Typography>
      <Typography variant="body2" fontWeight="medium">
        {value}
      </Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Phone color="primary" />
          Call Details
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        <Stack spacing={3}>
          {/* Patient Information Section */}
          <Box>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Patient Information
            </Typography>
            <Stack spacing={1.5}>
              <Box display="flex" flexWrap="wrap" gap={2}>
                <InfoItem
                  icon={<Person fontSize="small" color="action" />}
                  label="Patient"
                  value={data?.otherTabsData?.patientName || "N/A"}
                />
                <InfoItem
                  icon={<Person fontSize="small" color="action" />}
                  label="Guardian"
                  value={data?.otherTabsData?.guardiansName || "N/A"}
                />
              </Box>
              <Box display="flex" flexWrap="wrap" gap={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Badge fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    MRN:
                  </Typography>
                  <Chip
                    label={data?.otherTabsData?.mrn|| "N/A"}
                    size="small"
                    variant="outlined"
                  />
                </Box>
                <InfoItem
                  icon={<CalendarToday fontSize="small" color="action" />}
                  label="Age"
                  value={
                    data?.otherTabsData?.dob ? `${calculateAge(data?.otherTabsData?.dob )} years` : "N/A"
                  }
                />
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Call Information Section */}
          <Box>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Call Information
            </Typography>
            <Stack spacing={1.5}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2" color="text.secondary">
                  From:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {data?.callSendBy || "N/A"}
                </Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2" color="text.secondary">
                  To:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {data?.callSendTo || "N/A"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Reason for Call:
                </Typography>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.200",
                  }}
                >
                  <Typography variant="body2">
                    {data?.ReasonForCall || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Box>

          {/* Reply Input Section */}
          {isReplyMode && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Reply
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Enter your reply here..."
                  variant="outlined"
                />
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        {!isReplyMode && (
          <>
            <Button
              onClick={handleReplyFromDashboard}
              variant="contained"
              color="primary"
            >
              Reply from ipd-dashboard
            </Button>
            <Button
              onClick={handleReplyHere}
              variant="contained"
              color="primary"
            >
              Reply from here
            </Button>
          </>
        )}
        {isReplyMode && (
          <>
            <Button onClick={() => setIsReplyMode(false)} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleSubmitReply}
              variant="contained"
              color="primary"
              disabled={!replyText.trim()}
            >
              Submit Reply
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CallDetailDialog;
